<template>
  <div v-if="isOverviewOnlyRoute" class="topbar-escola-activator" style="cursor: default;">
    <b>Visão Geral</b>
    <s-icon 
      icon="mdiSchoolOutline" 
      size="medium" 
    />
  </div>
  <s-popover v-else position="top" animation="slide-y">
    <template #activator>
      <div class="topbar-escola-activator" style="cursor: pointer;">
        <b>
          {{ escola?.nome || 'Visão Geral' }}
        </b>
        <s-icon 
          v-if="escolas.length > 1"
          icon="mdiChevronDown" 
          size="medium" 
        />
        <s-icon 
          icon="mdiSchoolOutline" 
          size="medium" 
        />
      </div>
    </template>

    <template #content>
      <s-card background-color="bg-primary" shadow="medium" padding="x-small">
        <div class="topbar-escola-item"
          v-for="(item, index) in escolas" 
          :key="index"
          @click="setEscola(item)"
        >
          <b>{{ item.nome }}</b>
        </div>
        <div
          v-if="escolas.length > 1"
          class="topbar-escola-item"
          @click="resetEscola"
        >
          <b>Visão Geral</b>
        </div>
      </s-card>
    </template>
  </s-popover>
</template>

<script setup lang="ts">

import { SPopover, SIcon, SCard } from '@sieg-ad/componentes-vue';
import type { Escola } from '~/types/schemas/escola';
import { useRoute, useRouter } from 'vue-router';
import { useEscolaStore } from '~/stores/escola';
import { EscolaApi } from '~/api/escola/escola';
import { useAuthStore } from '~/stores/auth';

const escolaStore = useEscolaStore();
const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();

const { escola, escola_id } = storeToRefs(escolaStore);
const { user } = storeToRefs(authStore);

// REFS

const loadingEscolas = ref<boolean>(false);
const escolas = ref<Escola[]>([]);

// COMPUTEDS

const isOverviewOnlyRoute = computed(() => {
  const path = route.path.toLowerCase();
  return path.includes('/escolas') || path.includes('/agendas') || path.includes('/agenda') || path.includes('/cursos');
});

watch(
  () => route.path,
  () => {
    if (isOverviewOnlyRoute.value) {
      applyOverviewByRoute();
    }
  },
  { immediate: true }
);

// METHODS

function applyOverviewByRoute() {
  const hasSelectedEscola = Boolean(escola_id.value);
  escolaStore.resetEscola();
  if (hasSelectedEscola) {
    infoToast('Visão Geral selecionada');
  }
}

const loadEscolasUsuario = async () => {
  if (loadingEscolas.value) return;

  if (!user.value?.id) {
    escolas.value = [];
    return;
  }

  escolas.value = await loader<Escola>(EscolaApi, { ativo: true, 'educadores.usuario_id': user.value?.id }, loadingEscolas);

  if (isOverviewOnlyRoute.value) {
    applyOverviewByRoute();
    return;
  }
  
  if (escolas.value.length === 1) {
    escolaStore.setEscola(escolas.value[0]!);
  } else if (escola_id && escolas.value.length > 0) {
    const escolaItem = escolas.value.find((item) => item.id === escola_id.value);
    if (!escolaItem) resetEscola();
  }
}

const setEscola = (item: Escola) => {
  escolaStore.setEscola(item);
  localStorage.setItem('escola-toast-msg', `Escola selecionada: ${item.nome}`);
  window.location.href = router.currentRoute.value.fullPath;
}

const resetEscola = () => {
  if (escolas.value.length === 1) return;
  const isAlreadyInOverview = !escola_id.value;
  escolaStore.resetEscola();
  localStorage.setItem('escola-toast-msg', 'Visão Geral selecionada');
  if (!isAlreadyInOverview) {
    window.location.href = router.currentRoute.value.fullPath;
  }
}

// LIFECYCLE HOOKS

onMounted(() => {
  if (user.value?.id) loadEscolasUsuario();
  const toastMsg = localStorage.getItem('escola-toast-msg');
  if (toastMsg) {
    infoToast(toastMsg);
    localStorage.removeItem('escola-toast-msg');
  }
});


</script>

<style scoped>
.topbar-escola-activator {
  display: flex;
  align-items: center;
  color: var(--text-tertiary);
  font-size: 16px;
  padding: 0.5rem 1rem;
  gap: 0.5rem;
}
.topbar-escola-activator:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}
.topbar-escola-item {
  font-size: 16px;
  transition: background-color 0.3s ease;
  padding: 0.5rem 1rem;
  cursor: pointer;
}
.topbar-escola-item:hover {
  background-color: var(--bg-tertiary);
  border-radius: 4px;
}
.icone-cliente {
  width: auto;
  height: 32px;
  border-radius: 8px;
  object-fit: cover;
}
</style>